import { Fragment } from 'react';
import { Link } from 'react-router-dom';

import { useMutation } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';

import { Avatar } from '@components/avatar';
import { IconButton } from '@components/icon-button';
import { IconAssign } from '@components/icons/assign';
import { IconEdit } from '@components/icons/edit';
import { IconOnboarding } from '@components/icons/onboarding';
import { IconPersonRemove } from '@components/icons/person-remove';
import { IconTextShort } from '@components/icons/text-short';
import { Menu } from '@components/menu';
import { Select } from '@components/select';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';
import { Tooltip } from '@components/tooltip';
import { useIsMobile } from '@hooks/use-is-mobile';
import { abbreviateDate } from '@utils/date';

import { api } from 'api';

import { ApplicationLabels } from 'components/ApplicationLabels';
import { ApplicationOptions } from 'components/ApplicationOptions';
import { Box } from 'components/Box';
import { useModal } from 'features/modals';
import { usePermissions } from 'features/permissions';
import { useStore } from 'models/Provider';

export const ApplicationHeader = observer(({ application }) => {
  const { lang } = useStore();
  const { t } = useStore();
  const { job, hiredByUser, rejectedByUser, picture, name, isRejected, isHired } = application;
  const isMobile = useIsMobile();
  const { showError, showSuccess } = useToaster();
  const { hasRolePermission } = usePermissions();
  const { showModal } = useModal();
  const dt = abbreviateDate(application.date, lang);
  const dh = abbreviateDate(application.dateOfHire, lang);
  const dr = abbreviateDate(application.dateOfRejection, lang);

  function assign() {
    showModal('ASSIGN_APPLICANT', {
      applicationId: application.id,
      applicationName: application.name,
      applicationAssignedToId: application.assignedTo?.id,
    });
  }

  const removeAssignee = useMutation(() => api.applications.setAssignedTo(application.id, null), {
    onSuccess: () => {
      // Update store state for those not using react-query
      application.setAssignedTo(null);
      showSuccess(t('application.setassignedto.remove', application.name));
    },
    onError: () => {
      showError(
        t('application.setassignedto.error', application.name, application.assignedTo.name)
      );
    },
  });

  return (
    <Box flex flexDirection={isMobile ? 'column' : 'row'}>
      <Box alignItems="center" flex grow style={{ width: '100%' }}>
        <Avatar
          name={name}
          size={isMobile ? 'lg' : 'xl'}
          src={picture}
          color={isRejected ? 'red' : 'green'}
        />
        <Box ml={isMobile ? 8 : 16}>
          <Stack space={1}>
            <div>
              <Text font="heading2" text={name} />
              {!isMobile && (
                <Text
                  font="regular14"
                  color="purple60"
                  text={`${t('application.header.applied')} • ${dt}`}
                />
              )}
              {hiredByUser && isHired && (
                <Text
                  font="regular14"
                  color="purple60"
                  text={`${t('application.header.hired')} ${hiredByUser.name} • ${dh}`}
                />
              )}
              {rejectedByUser && isRejected && (
                <Text font="regular14" color="purple60">
                  {t('application.header.rejected')} {rejectedByUser.name} • {dr}
                </Text>
              )}
            </div>
            <ApplicationLabels application={application} />
          </Stack>
        </Box>
      </Box>

      {hasRolePermission('application:view-options') && (
        <Box flex w="100%" justifyContent="flex-end" mt={isMobile ? 24 : 0}>
          <Stack
            orientation="horizontal"
            space={2}
            justify="flex-end"
            style={{ width: isMobile ? '100%' : 'auto' }}
          >
            <Fragment>
              {!application.assignedTo ? (
                hasRolePermission('application:assign') && (
                  <IconButton
                    icon={<IconAssign />}
                    tooltip={t('application.tip.assign')}
                    rounded
                    onClick={hasRolePermission('application:assign') ? assign : null}
                  />
                )
              ) : (
                <Menu
                  buttonProps={{
                    style: { padding: '0' },
                    rounded: true,
                    iconLeft: (
                      <Tooltip
                        content={t('application.header.assignedto', application.assignedTo.name)}
                      >
                        <Avatar
                          src={application.assignedTo.picture}
                          name={application.assignedTo.name}
                          size="lg"
                        />
                      </Tooltip>
                    ),
                  }}
                >
                  <Menu.Item
                    onSelect={assign}
                    icon={<IconEdit />}
                    label={t('application.assignee.edit')}
                  />
                  <Menu.Item
                    onSelect={removeAssignee.mutate}
                    icon={<IconPersonRemove />}
                    label={t('application.assignee.remove')}
                  />
                </Menu>
              )}
            </Fragment>

            {application.isHired && !isMobile && (
              <Link to={application.onboardingUrl}>
                <IconButton icon={<IconOnboarding />} tooltip={t('application.gotoonboarding')} />
              </Link>
            )}
            {hasRolePermission('application:edit-stages') && (
              <Box w={isMobile ? '100%' : '17.5rem'}>
                <Select
                  isSearchable={false}
                  isClearable={false}
                  onChange={(stage) => application.changeStage(Number(stage), showModal)}
                  options={job.applicantJobStages}
                  defaultValue={application.jobStageId}
                  prefix={
                    <Box flex>
                      <IconTextShort />
                    </Box>
                  }
                />
              </Box>
            )}
            <ApplicationOptions
              data={application}
              redirect={application.job.detailUrl}
              grayBg
              t={t}
            />
          </Stack>
        </Box>
      )}
    </Box>
  );
});
